/**
 * DataList row component.
 *
 * Renders a single table row with:
 * - Row-level conditional formatting (background, text color, className)
 * - Selection checkbox (when selectable / bulk actions are configured)
 * - Data cells via DataListCell
 * - Row action column (inline buttons for 1-2 actions, dropdown for 3+)
 * - Row click handling with interactive element exclusion
 */

import * as React from 'react';
import { useCallback } from 'react';

import { Table } from '../../../base/data/Table/Table';
import { Checkbox } from '../../../base/inputs/Checkbox/Checkbox';
import { Button } from '../../../base/actions/Button/Button';
import { DropdownMenu } from '../../../base/actions/DropdownMenu/DropdownMenu';

import { DataListCell } from './DataListCell';
import { useDataListContext } from '../context/DataListContext';
import { evaluateRowRules, evaluateRowActionCondition } from '../utils/conditionalEvaluator';
import { safeString } from '../utils/cellRenderer';
import type { ColumnConfig } from '../types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface DataListRowProps {
  /** The row data object. */
  row: Record<string, unknown>;
  /** Zero-based index of the row within the current page. */
  index: number;
  /** Resolved visible columns with their keys. */
  columns: Array<{ key: string } & ColumnConfig>;
  /** Accessor to get the display value for a given column. */
  getCellValue: (row: Record<string, unknown>, columnKey: string) => unknown;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Interactive element selectors that should NOT trigger row click. */
const INTERACTIVE_SELECTOR = 'button, a, input, [role="menuitem"]';

// ---------------------------------------------------------------------------
// DataListRow Component
// ---------------------------------------------------------------------------

export function DataListRow({ row, index, columns, getCellValue }: Readonly<DataListRowProps>) {
  const { config, list } = useDataListContext();

  // -- Row conditional formatting --
  const rowStyles = evaluateRowRules(row, config.formatting?.rowRules ?? []);

  // -- Display options --
  const isStriped = config.display?.striped === true;
  const isHoverable = config.display?.hoverable !== false; // true by default

  // -- Row click handler --
  const handleRowClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't fire when clicking interactive elements
      const target = e.target as HTMLElement;
      if (target.closest(INTERACTIVE_SELECTOR)) return;
      list.handleRowClick(row);
    },
    [list, row],
  );

  const isClickable = Boolean(config.actions?.rowClick);
  const isSelectable =
    config.actions?.selectable ||
    (config.actions?.bulkActions?.length ?? 0) > 0;
  const rowActions = config.actions?.rowActions ?? [];

  // -- Filter row actions by condition --
  const visibleActions = rowActions.filter((action) =>
    evaluateRowActionCondition(row, action.condition),
  );

  // -- Build className --
  const rowClassName = [
    config.cssOverrides?.row,
    rowStyles.className,
    isClickable ? 'cursor-pointer' : '',
    isStriped && index % 2 === 1 ? 'bg-secondary-50/50' : '',
  ]
    .filter(Boolean)
    .join(' ') || undefined;

  // -- Build inline style for dynamic background/text color --
  const rowStyle: React.CSSProperties = {};
  if (rowStyles.backgroundColor) {
    rowStyle.backgroundColor = rowStyles.backgroundColor;
  }
  if (rowStyles.textColor) {
    rowStyle.color = rowStyles.textColor;
  }
  const hasRowStyle = Object.keys(rowStyle).length > 0;

  return (
    <Table.Row
      id={row.id == null ? undefined : safeString(row.id)}
      onClick={isClickable ? handleRowClick : undefined}
      className={rowClassName}
      hoverable={isHoverable}
      {...(hasRowStyle ? { style: rowStyle } : {})}
    >
      {/* Checkbox cell */}
      {isSelectable && (
        <Table.Cell>
          <Checkbox
            checked={list.isRowSelected(row)}
            onChange={() => list.toggleRowSelection(row)}
            aria-label={`Select row ${safeString(row.id)}`}
          />
        </Table.Cell>
      )}

      {/* Data cells */}
      {columns.map((column) => (
        <DataListCell
          key={column.key}
          column={column}
          value={getCellValue(row, column.key)}
          row={row}
        />
      ))}

      {/* Action column */}
      {visibleActions.length > 0 && (
        <Table.Cell>
          {visibleActions.length <= 2 ? (
            // Inline buttons for 1-2 actions
            <div className="flex items-center gap-1">
              {visibleActions.map((action) => (
                <Button
                  key={action.id}
                  variant={action.variant ?? 'ghost'}
                  size="sm"
                  onClick={() => list.executeRowAction(action.id, row)}
                  aria-label={action.label}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          ) : (
            // DropdownMenu for 3+ actions
            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button variant="ghost" size="sm" aria-label="Row actions">
                  {'\u22EF'}
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content>
                {visibleActions.map((action) => (
                  <DropdownMenu.Item
                    key={action.id}
                    onSelect={() => list.executeRowAction(action.id, row)}
                    destructive={action.variant === 'danger'}
                  >
                    {action.label}
                  </DropdownMenu.Item>
                ))}
              </DropdownMenu.Content>
            </DropdownMenu>
          )}
        </Table.Cell>
      )}

      {/* Empty action cell placeholder when row has no visible actions but other rows might */}
      {visibleActions.length === 0 && rowActions.length > 0 && (
        <Table.Cell />
      )}
    </Table.Row>
  );
}

DataListRow.displayName = 'DataListRow';
